import React, { FunctionComponent, ReactNode, MouseEvent, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

import { SeoLink_linkData } from './__generated__/SeoLink_linkData.graphql';

import styles from './SeoLink.scss';

export const PARTIALLY_LINKABLE_LINK = 'PARTIALLY_LINKABLE_LINK';
export const ALWAYS_LINKABLE_LINK = 'ALWAYS_LINKABLE_LINK';

type Props = {
    children: ReactNode;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    linkData: SeoLink_linkData;
    dataTn: string;
    className?: string;
    variant?: typeof PARTIALLY_LINKABLE_LINK | typeof ALWAYS_LINKABLE_LINK;
    style?: React.CSSProperties;
};

const SeoLinkComponent: FunctionComponent<Props> = ({
    children,
    linkData,
    onClick = () => {},
    dataTn,
    className,
    variant = ALWAYS_LINKABLE_LINK,
    style,
}) => {
    const { path, isLinkable } = linkData;
    const alwaysLinkableLink = useRef<HTMLAnchorElement>(null);
    const addHref = (): void => {
        if (path && alwaysLinkableLink?.current) {
            alwaysLinkableLink.current.href = path;
        }
    };
    const removeHref = (): void => {
        if (alwaysLinkableLink?.current) {
            alwaysLinkableLink.current.removeAttribute('href');
        }
    };

    if (!path) {
        return <>{children}</>;
    }

    if (isLinkable) {
        return (
            <a href={path} onClick={onClick} data-tn={dataTn} className={className} style={style}>
                {children}
            </a>
        );
    }

    if (variant === PARTIALLY_LINKABLE_LINK) {
        return (
            <span onClick={onClick} data-tn={dataTn} className={className} style={style}>
                {children}
            </span>
        );
    }

    if (variant === ALWAYS_LINKABLE_LINK) {
        return (
            <a
                onClick={(e: MouseEvent<HTMLElement>) => {
                    onClick(e);
                    addHref();
                }}
                onMouseOver={addHref}
                onMouseOut={removeHref}
                onTouchStart={addHref}
                onTouchEnd={removeHref}
                data-tn={dataTn}
                className={classnames(className, styles.jsLink)}
                style={style}
                ref={alwaysLinkableLink}
            >
                {children}
            </a>
        );
    }

    return null;
};

export const SeoLink = createFragmentContainer(SeoLinkComponent, {
    linkData: graphql`
        fragment SeoLink_linkData on LinkData {
            path
            isLinkable
        }
    `,
});
